/**
 * Metric vocabulary shared across the data dashboard: which metrics exist, the
 * shape every metrics route answers with, and the display rules the API does not
 * send.
 *
 * These live in config rather than in an API hook because pages, utils and three
 * separate route clients all need them. Keeping them in one hook would mean a
 * page importing its metric types from a route client it never calls, and would
 * tie the vocabulary to the lifetime of whichever client happened to declare it.
 *
 * Nothing here performs I/O — see the `use-metrics-*-v2-api` hooks for the
 * routes themselves.
 */

// ── Response shape ─────────────────────────────────────────────────────────────

/**
 * How a value is rendered. The API returns raw values with no formatting hint,
 * so the client decides.
 */
export type MetricFormat = 'number' | 'percent';

/**
 * One metric's value, as every metrics route reports it.
 *
 * `format` is attached client-side by {@link getMetricFormat}; it is not part of
 * any response payload. Each route validates its own payload with zod — the
 * schemas differ in how entries are wrapped, not in the fields here.
 */
export interface MetricV2Response {
  value: string | null;
  label: string;
  toolTip?: string;
  dateOfAssessment?: string;
  format?: MetricFormat;
}

// ── Supported metric types ─────────────────────────────────────────────────────

export type MetricV2Type =
  | 'TOTAL_STUDENT_SESSIONS'
  | 'TOTAL_STUDENT_MINUTES'
  | 'TOTAL_MINUTES_READING'
  | 'TOTAL_LITBITS_EARNED'
  | 'LEXILE_MEASURE'
  | 'GE_SCORE'
  | 'AVG_ACTIVITY_SCORE'
  | 'ACTIVITIES_COMPLETED'
  | 'CONTENT_COMPLETIONS'
  | 'CONTENT_VIEWS'
  | 'DIGITAL_MINUTES_READ'
  | 'MANUALLY_ENTERED_MINUTES'
  | 'LEXILE_BOY_ASSESSMENTS_COMPLETE'
  | 'LEXILE_MOY_ASSESSMENTS_COMPLETE'
  | 'LEXILE_EOY_ASSESSMENTS_COMPLETE'
  | 'GE_FIRST_ASSESSMENTS_COMPLETE'
  | 'GE_LAST_ASSESSMENTS_COMPLETE'
  | 'TEXTS_COMPLETED'
  | 'SHORT_READ_CARDS_COMPLETED'
  | 'READ_AND_RECORD_ACCURACY';

// ── Percent-formatted metrics ──────────────────────────────────────────────────

const PERCENT_METRICS: MetricV2Type[] = [
  'AVG_ACTIVITY_SCORE',
  'READ_AND_RECORD_ACCURACY',
];

/**
 * How a metric's value is rendered. Exported rather than the list itself so
 * every route formats a given metric identically — the response carries no
 * format, so each client has to derive the same answer.
 */
export function getMetricFormat(metricType: MetricV2Type): MetricFormat {
  return PERCENT_METRICS.includes(metricType) ? 'percent' : 'number';
}

// ── Student-level metric types ─────────────────────────────────────────────────

/**
 * Metrics that report on a single student rather than a roster, and so require
 * `studentId` instead of `teacherIds`.
 */
export const STUDENT_LEVEL_METRICS: MetricV2Type[] = [
  'LEXILE_MEASURE',
  'GE_SCORE',
];
